/**
 * THE BAR — inside a saloon, before the hunt.
 *
 * This screen exists to make the room feel like a room. It is the difference
 * between "lobby: 4/8 players ready" and walking in to find three people
 * already at the rail with their names on stools. The information is the same;
 * the game is not.
 */

import { useState } from 'react';
import type { Snapshot } from '../net/connection.js';
import { leaveSaloon, setReady, startHunt } from '../net/connection.js';
import { seatColor } from '../hunt/art.js';
import { Pip } from '../ui/Pip.js';
import { modeWord, type Settings } from '../ui/settings.js';

interface Props {
  snap: Snapshot;
  settings: Settings;
  onTakeGun: () => Promise<void>;
  onOpenSettings: () => void;
  onRange: () => void;
}

export function Bar({ snap, settings, onTakeGun, onOpenSettings, onRange }: Props) {
  const view = snap.view!;
  const me = view.seats.find((s) => s.id === snap.seatId);
  const isHost = !!me?.isHost;
  const anted = view.seats.filter((s) => s.ready && s.online).length;
  const [copied, setCopied] = useState(false);

  const share = async () => {
    const url = `${location.origin}/?s=${view.code}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: '2 Buck Saloon', text: `Get in here — ${view.code}`, url });
        return;
      }
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // The share sheet was dismissed, or the clipboard is unavailable in this
      // context. The code is on screen either way, which is the real fallback.
    }
  };

  const ante = async () => {
    if (!me?.ready) await onTakeGun();
    setReady(!me?.ready);
  };

  return (
    <main className="room">
      <div className="row between">
        <div>
          <p className="tiny muted m-0">You are in</p>
          <p className="marquee m-0 fs-lg">
            <span className="mono">{view.code}</span>
          </p>
        </div>
        <div className="row">
          <button className="btn" onClick={share}>
            {copied ? 'Copied' : 'Invite'}
          </button>
          <button className="btn btn-ghost" onClick={leaveSaloon}>
            Leave
          </button>
        </div>
      </div>

      <div className="panel mt-md">
        <p className="tiny muted m-0">
          At the rail — {view.seats.length} {view.seats.length === 1 ? 'hunter' : 'hunters'}
          {view.open ? '' : ' · private room'}
        </p>
        <div className="stools">
          {view.seats.map((s) => (
            <div
              key={s.id}
              className={`stool${s.id === snap.seatId ? ' is-me' : ''}${s.online ? '' : ' is-off'}`}
            >
              <Chip hue={s.hue} />
              <span className="name grow">{s.handle}</span>
              {s.isHost && <span className="badge badge-host">Round</span>}
              {s.ready && <span className="badge badge-ante">Ante</span>}
            </div>
          ))}
        </div>
        {view.seats.length === 1 && (
          <p className="muted fs-md mt-sm mb-0">
            Nobody else yet. Hit <strong>Invite</strong> — or start solo, the herd does not care.
          </p>
        )}
      </div>

      {!settings.trained && (
        <div className="panel mt-md">
          <p className="m-0 fs-md">
            <strong>First time?</strong> Fifteen seconds on the range and you will know what you are doing.
          </p>
          <button className="btn btn-wide mt-sm" onClick={onRange}>
            Take the practice range
          </button>
        </div>
      )}

      <div className="panel mt-md">
        <button className={`btn btn-big btn-wide ${me?.ready ? '' : 'btn-primary'}`} onClick={ante}>
          {me?.ready ? 'Put the gun back' : 'Drop $2 · take the gun'}
        </button>
        <p className="tiny muted mt-xs mb-0">
          {me?.ready
            ? `Aiming with ${modeWord(settings.mode)} — change it in settings, any time, even mid-wave.`
            : settings.mode === 'gyro'
              ? 'Anteing up asks for motion access so you can aim by pointing. You can say no and use your thumb instead.'
              : `Aiming with ${modeWord(settings.mode)}. Change it in settings, any time, even mid-wave.`}
        </p>

        {isHost && (
          <>
            <hr className="rule" />
            <button className="btn btn-primary btn-big btn-wide" disabled={anted === 0} onClick={startHunt}>
              {anted === 0 ? 'Waiting for somebody to ante' : `Start the hunt · ${anted} in`}
            </button>
            <p className="tiny muted mt-xs mb-0">
              Ten waves across five sites. Anyone who has not anted up can still watch.
            </p>
          </>
        )}
        {!isHost && (
          <p className="muted fs-md mt-md mb-0">
            Waiting on <strong>{view.seats.find((s) => s.isHost)?.handle ?? 'the house'}</strong> to call it.
          </p>
        )}
      </div>

      <div className="row between mt-md">
        <Pip status={snap.status} rtt={snap.rtt} />
        <div className="row">
          <button className="btn btn-ghost" onClick={onRange}>
            Range
          </button>
          <button className="btn btn-ghost" onClick={onOpenSettings}>
            Settings
          </button>
        </div>
      </div>
    </main>
  );
}

/**
 * A seat's colour dot. The colour is drawn with an SVG `fill` attribute rather
 * than a style attribute — presentation attributes are markup, not CSS, so
 * this needs nothing from `style-src` at all.
 */
export function Chip({ hue, size = 14 }: { hue: number; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden="true" className="chip-svg">
      <circle cx="8" cy="8" r="7" fill={seatColor(hue)} stroke="rgba(0,0,0,0.55)" strokeWidth="1.5" />
    </svg>
  );
}
